import { cn } from "@/lib/utils";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { useAuth } from "components/Reusable/AuthProvider";
import dayjs from "dayjs";
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Combine,
  Edit,
  ExternalLink,
  GripVertical,
  Link,
  Plus,
  RotateCcw,
  Save,
  TableOfContents,
  Trash2,
  X,
} from "lucide-react";
import {
  LegalDocument,
  LegalDocumentSection,
  LegalDocumentSectionChunk,
} from "models/legalDocument";
import { getCategoryLabelKey, getTypeLabelKey, LEGAL_CATEGORIES, LEGAL_TYPES } from "models/legalDomainConstants";
import { PaginatedResponse } from "models/pagination";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { AxiosHttpClient } from "services/AxiosHttpService";
import { v4 as uuid } from "uuid";

// DTO pour la sauvegarde
export interface DocumentUpdateDTO {
  documentId: string;
  sections: SectionUpdateDTO[];
  metadata?: {
    title?: string;
    description?: string;
    version?: string;
    authors?: string[];
    type?: string;
    category?: string;
    tags?: string[];
    validFrom?: string | null;
    validUntil?: string | null;
  };
}

export interface SectionUpdateDTO {
  id: string;
  orderIndex: number;
  parentId?: string;
  chunks: ChunkUpdateDTO[];
  references: string[]; // IDs des sections référencées
  operation: "CREATE" | "UPDATE" | "DELETE";
}

export interface ChunkUpdateDTO {
  id: string;
  position: number;
  content: string;
  operation: "CREATE" | "UPDATE" | "DELETE";
}

// Service pour récupérer les documents avec recherche et pagination
const searchDocuments = async (
  searchQuery: string = "",
  page: number = 0,
  size: number = 20
): Promise<PaginatedResponse<LegalDocument>> => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
    sort: "title", // Tri par titre pour l'autocomplete
  });

  if (searchQuery.trim()) {
    params.append("search", searchQuery);
    params.append("searchType", "keywords");
  }

  try {
    const response = await AxiosHttpClient.get<
      PaginatedResponse<LegalDocument>
    >(`api/v1/docuveritas/legal-documents?${params.toString()}`);
    return response;
  } catch (error) {
    console.error("Erreur lors de la recherche de documents:", error);
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      number: 0,
      size: size,
      first: true,
      last: true,
      numberOfElements: 0,
    };
  }
};

export default function DocumentViewer() {
  const { docId } = useParams<{ docId: string }>();
  const { search: locationSearch } = useLocation();
  const [legalDocument, setLegalDocument] = useState<LegalDocument | null>(
    null
  );
  const [originalDocument, setOriginalDocument] =
    useState<LegalDocument | null>(null); // Version originale pour comparaison
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [expandedTocItems, setExpandedTocItems] = useState<Set<string>>(
    new Set(["root"])
  );
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false); // Indicateur de modifications non sauvegardées
  const [isSaving, setIsSaving] = useState<boolean>(false); // Indicateur de sauvegarde en cours
  const [isRecomputingCrossRefs, setIsRecomputingCrossRefs] =
    useState<boolean>(false);
  const [editingChunk, setEditingChunk] = useState<string | null>(null);
  const [chunkContent, setChunkContent] = useState<string>("");
  const [newSectionParent, setNewSectionParent] = useState<string | null>(null);
  const [newSectionContent, setNewSectionContent] = useState<string>("");
  const [hoveredChunk, setHoveredChunk] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );
  const [showDeleteChunkConfirm, setShowDeleteChunkConfirm] = useState<
    string | null
  >(null);
  const [showNewChunkInput, setShowNewChunkInput] = useState<string | null>(
    null
  );
  const [newChunkInputContent, setNewChunkInputContent] = useState<string>("");
  const [showNewRootSectionInput, setShowNewRootSectionInput] =
    useState<boolean>(false);
  const [newRootSectionContent, setNewRootSectionContent] =
    useState<string>("");
  const [managingReferences, setManagingReferences] = useState<string | null>(
    null
  );
  const [newReferenceInput, setNewReferenceInput] = useState<string>("");
  const [availableSections, setAvailableSections] = useState<
    LegalDocumentSection[]
  >([]);
  const [availableDocuments, setAvailableDocuments] = useState<LegalDocument[]>(
    []
  );
  const [selectedDocumentForReference, setSelectedDocumentForReference] =
    useState<string>("");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [metadataAuthorInput, setMetadataAuthorInput] = useState<string>("");

  // États pour l'autocomplete avec infinite scroll
  const [documentSearchQuery, setDocumentSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<LegalDocument[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchPage, setSearchPage] = useState<number>(0);
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.roles?.some((role: string) => role.includes("ADMIN"));

  // Drag & drop state (mode édition uniquement)
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
  const [dropTargetSectionId, setDropTargetSectionId] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | 'child' | null>(null);

  // Fonction pour rechercher des documents avec debounce
  const performDocumentSearch = async (
    query: string,
    page: number = 0,
    append: boolean = false
  ) => {
    if (page === 0) {
      setIsSearching(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await searchDocuments(query, page, 20);

      if (append) {
        setSearchResults((prev) => [...prev, ...response.content]);
      } else {
        setSearchResults(response.content);
      }

      setHasMoreResults(!response.last);
      setSearchPage(page);
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  };

  // Effect pour la recherche avec debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (documentSearchQuery.trim() || documentSearchQuery === "") {
        performDocumentSearch(documentSearchQuery.trim(), 0, false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [documentSearchQuery]);

  // Fonction pour charger plus de résultats
  const loadMoreResults = () => {
    if (!isLoadingMore && hasMoreResults) {
      performDocumentSearch(documentSearchQuery.trim(), searchPage + 1, true);
    }
  };

  // État pour contrôler l'affichage du dropdown
  const [showSearchDropdown, setShowSearchDropdown] = useState<boolean>(false);

  // Effect pour fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".search-dropdown-container")) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!docId) {
      setError("ID du document manquant");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    AxiosHttpClient.get<LegalDocument>(
      `api/v1/docuveritas/legal-documents/${docId}`
    )
      .then((document) => {
        setLegalDocument(document);
        console.log("document", document);
        console.log("structuredClone(document)", structuredClone(document));
        setOriginalDocument(structuredClone(document)); // Simple et efficace !
      })
      .catch((err) => {
        console.error("Erreur lors de la récupération du document:", err);
        setError("Erreur lors du chargement du document");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [docId]);

  // Auto-navigate to a section/chunk passed as URL query params
  useEffect(() => {
    if (!legalDocument) return;
    const params = new URLSearchParams(locationSearch);
    const sectionId = params.get("section");
    if (sectionId) {
      navigateToSection(sectionId);
      return;
    }

    const chunkId = params.get("chunk");
    if (!chunkId) return;
    const allSections: LegalDocumentSection[] = [];
    const collect = (sections: LegalDocumentSection[]) => {
      sections.forEach((s) => { allSections.push(s); collect(s.children); });
    };
    collect(legalDocument.rootSections);
    const targetSection = allSections.find((s) =>
      s.contentChunks.some((c) => c.id.toLowerCase() === chunkId.toLowerCase())
    );
    if (targetSection) {
      navigateToSection(targetSection.id);
    }
  }, [legalDocument, locationSearch]);

  // Avertissement avant fermeture si modifications non sauvegardées
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue =
          "Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const toggleTocItem = (sectionId: string) => {
    const newExpanded = new Set(expandedTocItems);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedTocItems(newExpanded);
  };

  const toggleAllSections = () => {
    if (!legalDocument) return;
    const allIds = getAllSections(legalDocument.rootSections)
      .filter((s) => s.children.length > 0)
      .map((s) => s.id);
    const allExpanded = allIds.every((id) => expandedSections.has(id));
    setExpandedSections(allExpanded ? new Set() : new Set(allIds));
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Fonction pour trouver tous les parents d'une section
  const findSectionParents = (
    targetSectionId: string,
    sections: LegalDocumentSection[],
    parents: string[] = []
  ): string[] => {
    for (const section of sections) {
      if (section.id === targetSectionId) {
        return parents;
      }
      if (section.children.length > 0) {
        const found = findSectionParents(targetSectionId, section.children, [
          ...parents,
          section.id,
        ]);
        if (found.length > 0) {
          return found;
        }
      }
    }
    return [];
  };

  // Fonction pour développer les parents et naviguer vers une section
  const navigateToSection = (sectionId: string) => {
    if (!legalDocument) return;

    // Trouver tous les parents de la section
    const parentIds = findSectionParents(sectionId, legalDocument.rootSections);

    // Développer tous les parents dans la TOC
    const newExpandedTocItems = new Set(expandedTocItems);
    parentIds.forEach((parentId) => {
      newExpandedTocItems.add(parentId);
    });
    setExpandedTocItems(newExpandedTocItems);

    // Développer tous les parents dans le contenu principal
    const newExpandedSections = new Set(expandedSections);
    parentIds.forEach((parentId) => {
      newExpandedSections.add(parentId);
    });
    newExpandedSections.add(sectionId); // Développer aussi la section elle-même si elle a des enfants
    setExpandedSections(newExpandedSections);

    // Scroller vers la section
    setTimeout(() => {
      scrollToSection(sectionId);
    }, 300); // Délai pour laisser le temps aux éléments de se développer
  };

  // Fonctions d'édition
  const generateId = () => uuid();

  // Marquer le document comme modifié
  const markAsDirty = () => {
    if (!isDirty) {
      setIsDirty(true);
    }
  };

  // Fonction pour sauvegarder les modifications
  const handleSave = () => {
    if (!legalDocument || !originalDocument || !isDirty) return;

    setIsSaving(true);
    const updateDTO = generateUpdateDTO(originalDocument, legalDocument);

    // Appel API de sauvegarde
    AxiosHttpClient.put<LegalDocument>(
      `api/v1/docuveritas/legal-documents/${legalDocument.id}`,
      updateDTO
    )
      .then((response) => {
        // Mettre à jour le document avec la réponse du serveur
        setLegalDocument(response);
        setOriginalDocument(structuredClone(response));
        setIsDirty(false);

        // Afficher un message de succès
        alert("Document sauvegardé avec succès !");
      })
      .catch((error) => {
        console.error("Erreur lors de la sauvegarde:", error);
        alert("Erreur lors de la sauvegarde du document");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleRecomputeCrossReferences = async () => {
    if (!legalDocument) return;
    if (isEditMode) {
      alert(
        "Veuillez quitter le mode édition avant de recalculer les cross-références."
      );
      return;
    }

    const confirmed = window.confirm(
      "Relancer uniquement le calcul des cross-références pour ce document ?"
    );
    if (!confirmed) return;

    setIsRecomputingCrossRefs(true);
    try {
      const result = await AxiosHttpClient.post<{
        processedChunks: number;
        detectedCrossRefs: number;
        resolvedCrossRefs: number;
        pendingCrossRefs: number;
        ambiguousCrossRefs: number;
        unresolvableCrossRefs: number;
        unparseableCrossRefs: number;
        unresolvedInternalCrossRefs: number;
      }>(
        `api/v1/docuveritas/legal-documents/${legalDocument.id}/cross-references/recompute`,
        {}
      );

      const refreshed = await AxiosHttpClient.get<LegalDocument>(
        `api/v1/docuveritas/legal-documents/${legalDocument.id}`
      );
      setLegalDocument(refreshed);
      setOriginalDocument(structuredClone(refreshed));

      alert(
        `Cross-références recalculées.\n` +
          `Chunks traités: ${result.processedChunks}\n` +
          `Références détectées: ${result.detectedCrossRefs}\n` +
          `Résolues: ${result.resolvedCrossRefs}\n` +
          `Pending: ${result.pendingCrossRefs}\n` +
          `Ambiguës: ${result.ambiguousCrossRefs}\n` +
          `Non résolubles: ${result.unresolvableCrossRefs}\n` +
          `Non parseables: ${result.unparseableCrossRefs}\n` +
          `Internes non résolues: ${result.unresolvedInternalCrossRefs}`
      );
    } catch (error) {
      console.error("Erreur lors du recalcul des cross-références:", error);
      alert("Erreur lors du recalcul des cross-références");
    } finally {
      setIsRecomputingCrossRefs(false);
    }
  };

  // Fonction pour annuler les modifications (retour au draft original)
  const handleDiscardChanges = () => {
    if (!originalDocument) return;

    if (
      window.confirm(
        "Êtes-vous sûr de vouloir annuler toutes les modifications ?"
      )
    ) {
      setLegalDocument(structuredClone(originalDocument));
      setIsDirty(false);

      // Réinitialiser les états d'édition
      setEditingChunk(null);
      setChunkContent("");
      setNewSectionParent(null);
      setNewSectionContent("");
      setHoveredChunk(null);
      setShowDeleteConfirm(null);
      setShowDeleteChunkConfirm(null);
      setShowNewChunkInput(null);
      setNewChunkInputContent("");
      setShowNewRootSectionInput(false);
      setNewRootSectionContent("");
      setManagingReferences(null);
      setNewReferenceInput("");
      setAvailableSections([]);
      setAvailableDocuments([]);
      setSelectedDocumentForReference("");
      setMetadataAuthorInput("");
    }
  };

  // Générer le DTO pour la sauvegarde
  const generateUpdateDTO = (
    original: LegalDocument,
    current: LegalDocument
  ): DocumentUpdateDTO => {
    const dto: DocumentUpdateDTO = {
      documentId: current.id,
      sections: [],
      metadata: {},
    };

    // Build flat maps: id → { section, parentId, orderIndex }
    type FlatEntry = { section: LegalDocumentSection; parentId: string | undefined; orderIndex: number };
    const flatOrig = new Map<string, FlatEntry>();
    const flatCurr = new Map<string, FlatEntry>();

    const flatten = (sections: LegalDocumentSection[], parentId: string | undefined, map: Map<string, FlatEntry>) => {
      sections.forEach((s, idx) => {
        map.set(s.id, { section: s, parentId, orderIndex: idx });
        flatten(s.children, s.id, map);
      });
    };
    flatten(original.rootSections, undefined, flatOrig);
    flatten(current.rootSections, undefined, flatCurr);

    const hasChunkChanges = (
      origChunks: LegalDocumentSectionChunk[],
      currChunks: LegalDocumentSectionChunk[]
    ): boolean => {
      if (origChunks.length !== currChunks.length) return true;
      return origChunks.some((o, i) => {
        const c = currChunks[i];
        return !c || o.content !== c.content || o.position !== c.position;
      });
    };

    const buildChunkDiffs = (
      origChunks: LegalDocumentSectionChunk[],
      currChunks: LegalDocumentSectionChunk[]
    ): ChunkUpdateDTO[] => {
      const chunks: ChunkUpdateDTO[] = [];
      origChunks.forEach((o) => {
        if (!currChunks.find((c) => c.id === o.id)) {
          chunks.push({ id: o.id, position: o.position, content: o.content, operation: "DELETE" });
        }
      });
      currChunks.forEach((c, ci) => {
        const o = origChunks.find((x) => x.id === c.id);
        if (!o) {
          chunks.push({ id: c.id, position: ci, content: c.content, operation: "CREATE" });
        } else if (o.content !== c.content || o.position !== ci) {
          chunks.push({ id: c.id, position: ci, content: c.content, operation: "UPDATE" });
        }
      });
      return chunks;
    };

    // DELETEs: sections in original but not in current
    for (const [id, entry] of flatOrig) {
      if (!flatCurr.has(id)) {
        dto.sections.push({
          id,
          orderIndex: entry.orderIndex,
          parentId: entry.parentId,
          chunks: [],
          references: [],
          operation: "DELETE",
        });
      }
    }

    // CREATEs and UPDATEs
    for (const [id, currEntry] of flatCurr) {
      const origEntry = flatOrig.get(id);
      if (!origEntry) {
        // New section
        dto.sections.push({
          id,
          orderIndex: currEntry.orderIndex,
          parentId: currEntry.parentId,
          chunks: currEntry.section.contentChunks.map((c, ci) => ({
            id: c.id,
            position: ci,
            content: c.content,
            operation: "CREATE" as const,
          })),
          references: currEntry.section.references.map((r) => r.id),
          operation: "CREATE",
        });
      } else {
        // Existing section — check for changes
        const parentChanged = origEntry.parentId !== currEntry.parentId;
        const orderChanged = origEntry.orderIndex !== currEntry.orderIndex;
        const refsChanged =
          JSON.stringify(origEntry.section.references.map((r) => r.id).sort()) !==
          JSON.stringify(currEntry.section.references.map((r) => r.id).sort());
        const chunksChanged = hasChunkChanges(origEntry.section.contentChunks, currEntry.section.contentChunks);

        if (parentChanged || orderChanged || refsChanged || chunksChanged) {
          dto.sections.push({
            id,
            orderIndex: currEntry.orderIndex,
            parentId: currEntry.parentId,
            chunks: buildChunkDiffs(origEntry.section.contentChunks, currEntry.section.contentChunks),
            references: currEntry.section.references.map((r) => r.id),
            operation: "UPDATE",
          });
        }
      }
    }

    // Vérifier les changements de métadonnées
    if (original.title !== current.title) dto.metadata!.title = current.title;
    if (original.description !== current.description)
      dto.metadata!.description = current.description;
    if (original.version !== current.version)
      dto.metadata!.version = current.version;
    if (JSON.stringify(original.authors) !== JSON.stringify(current.authors))
      dto.metadata!.authors = current.authors;
    if (original.type !== current.type) dto.metadata!.type = current.type;
    if (original.category !== current.category) dto.metadata!.category = current.category;
    if (JSON.stringify(original.tags) !== JSON.stringify(current.tags))
      dto.metadata!.tags = current.tags;
    const origVF = original.validFrom ? dayjs(original.validFrom).format("YYYY-MM-DD") : null;
    const currVF = current.validFrom ? dayjs(current.validFrom).format("YYYY-MM-DD") : null;
    if (origVF !== currVF) dto.metadata!.validFrom = currVF;
    const origVU = original.validUntil ? dayjs(original.validUntil).format("YYYY-MM-DD") : null;
    const currVU = current.validUntil ? dayjs(current.validUntil).format("YYYY-MM-DD") : null;
    if (origVU !== currVU) dto.metadata!.validUntil = currVU;

    return dto;
  };

  const createNewSection = (parentId: string, content: string) => {
    if (!legalDocument) return;

    const findAndUpdateSection = (
      sections: LegalDocumentSection[]
    ): boolean => {
      for (const section of sections) {
        if (section.id === parentId) {
          const newSection: LegalDocumentSection = {
            id: generateId(),
            orderIndex: section.children.length,
            contentChunks: content
              ? [
                  {
                    id: generateId(),
                    position: 0,
                    content,
                    embedding: "",
                    section: {} as LegalDocumentSection,
                  },
                ]
              : [],
            parent: section,
            children: [],
            references: [],
            referencedBy: [],
            document: legalDocument,
          };

          // Lier le chunk à sa section
          if (newSection.contentChunks.length > 0) {
            newSection.contentChunks[0].section = newSection;
          }

          section.children.push(newSection);
          return true;
        }
        if (findAndUpdateSection(section.children)) return true;
      }
      return false;
    };

    findAndUpdateSection(legalDocument.rootSections);
    setLegalDocument({ ...legalDocument });
    setNewSectionParent(null);
    setNewSectionContent("");
    markAsDirty();
  };

  const addChunkToSection = (sectionId: string, content: string) => {
    if (!legalDocument) return;

    const findAndUpdateSection = (
      sections: LegalDocumentSection[]
    ): boolean => {
      for (const section of sections) {
        if (section.id === sectionId) {
          const newChunk: LegalDocumentSectionChunk = {
            id: generateId(),
            position: section.contentChunks.length,
            content,
            embedding: "",
            section,
          };
          section.contentChunks.push(newChunk);
          return true;
        }
        if (findAndUpdateSection(section.children)) return true;
      }
      return false;
    };

    findAndUpdateSection(legalDocument.rootSections);
    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };
  const updateChunk = (chunkId: string, newContent: string) => {
    if (!legalDocument) return;

    const findAndUpdateChunk = (sections: LegalDocumentSection[]): boolean => {
      for (const section of sections) {
        const chunk = section.contentChunks.find((c) => c.id === chunkId);
        if (chunk) {
          chunk.content = newContent;
          return true;
        }
        if (findAndUpdateChunk(section.children)) return true;
      }
      return false;
    };

    findAndUpdateChunk(legalDocument.rootSections);
    setLegalDocument({ ...legalDocument });
    setEditingChunk(null);
    setChunkContent("");
    markAsDirty();
  };

  const deleteChunk = (chunkId: string) => {
    if (!legalDocument) return;

    const findAndDeleteChunk = (sections: LegalDocumentSection[]): boolean => {
      for (const section of sections) {
        const chunkIndex = section.contentChunks.findIndex(
          (c) => c.id === chunkId
        );
        if (chunkIndex !== -1) {
          section.contentChunks.splice(chunkIndex, 1);
          // Réorganiser les positions
          section.contentChunks.forEach((chunk, index) => {
            chunk.position = index + 1;
          });
          return true;
        }
        if (findAndDeleteChunk(section.children)) return true;
      }
      return false;
    };

    findAndDeleteChunk(legalDocument.rootSections);
    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };

  const deleteSection = (sectionId: string) => {
    if (!legalDocument) return;

    const findAndDeleteSection = (
      sections: LegalDocumentSection[],
      parent?: LegalDocumentSection
    ): boolean => {
      const sectionIndex = sections.findIndex((s) => s.id === sectionId);
      if (sectionIndex !== -1) {
        sections.splice(sectionIndex, 1);
        // Réorganiser les indices
        sections.forEach((section, index) => {
          section.orderIndex = index + 1;
        });
        return true;
      }

      for (const section of sections) {
        if (findAndDeleteSection(section.children, section)) return true;
      }
      return false;
    };

    findAndDeleteSection(legalDocument.rootSections);
    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };

  // Fonctions de gestion des références
  const getAllSections = (
    sections: LegalDocumentSection[]
  ): LegalDocumentSection[] => {
    let allSections: LegalDocumentSection[] = [];
    sections.forEach((section) => {
      allSections.push(section);
      allSections = allSections.concat(getAllSections(section.children));
    });
    return allSections;
  };

  const getAllSectionsFromDocument = (
    document: LegalDocument
  ): LegalDocumentSection[] => {
    return getAllSections(document.rootSections);
  };

  /** Retourne true si candidateId est ancestorId lui-même ou l'un de ses descendants. */
  const isDescendantOrSelf = (candidateId: string, ancestorId: string): boolean => {
    if (candidateId === ancestorId) return true;
    if (!legalDocument) return false;
    const findAncestorAndCheck = (sections: LegalDocumentSection[]): boolean | null => {
      for (const s of sections) {
        if (s.id === ancestorId) {
          return getAllSections(s.children).some((d) => d.id === candidateId);
        }
        const result = findAncestorAndCheck(s.children);
        if (result !== null) return result;
      }
      return null;
    };
    return findAncestorAndCheck(legalDocument.rootSections) ?? false;
  };

  /** Déplace la section draggedId pour en faire un enfant de newParentId. */
  const moveSectionToNewParent = (draggedId: string, newParentId: string) => {
    if (!legalDocument || draggedId === newParentId) return;
    // Sécurité : on ne peut pas déposer sur un descendant de soi-même
    if (isDescendantOrSelf(newParentId, draggedId)) return;

    let draggedSection: LegalDocumentSection | null = null;

    // 1. Retirer la section de son emplacement actuel
    const removeFromTree = (sections: LegalDocumentSection[]): boolean => {
      const idx = sections.findIndex((s) => s.id === draggedId);
      if (idx !== -1) {
        [draggedSection] = sections.splice(idx, 1);
        sections.forEach((s, i) => { s.orderIndex = i + 1; });
        return true;
      }
      return sections.some((s) => removeFromTree(s.children));
    };
    removeFromTree(legalDocument.rootSections);
    if (!draggedSection) return;
    const sectionToMove: LegalDocumentSection = draggedSection;

    // 2. L'ajouter comme dernier enfant du nouveau parent
    const addToParent = (sections: LegalDocumentSection[]): boolean => {
      for (const s of sections) {
        if (s.id === newParentId) {
          draggedSection!.parent = s;
          draggedSection!.orderIndex = s.children.length + 1;
          s.children.push(draggedSection!);
          return true;
        }
        if (addToParent(s.children)) return true;
      }
      return false;
    };
    const added = addToParent(legalDocument.rootSections);
    if (!added) {
      // Filet de sécurité : si le parent cible est introuvable, on remet la
      // section à la racine pour ne jamais la perdre (sinon elle serait
      // supprimée en BD à la sauvegarde).
      sectionToMove.parent = undefined;
      sectionToMove.orderIndex = legalDocument.rootSections.length + 1;
      legalDocument.rootSections.push(sectionToMove);
    }

    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };

  /** Réordonne la section draggedId avant ou après targetId (frères dans le même parent ou changement de parent). */
  const moveSectionRelativeToSibling = (draggedId: string, targetId: string, position: 'before' | 'after') => {
    if (!legalDocument || draggedId === targetId) return;
    if (isDescendantOrSelf(targetId, draggedId)) return;

    let draggedSection: LegalDocumentSection | null = null;

    const removeFromTree = (sections: LegalDocumentSection[]): boolean => {
      const idx = sections.findIndex((s) => s.id === draggedId);
      if (idx !== -1) {
        [draggedSection] = sections.splice(idx, 1);
        sections.forEach((s, i) => { s.orderIndex = i + 1; });
        return true;
      }
      return sections.some((s) => removeFromTree(s.children));
    };
    removeFromTree(legalDocument.rootSections);
    if (!draggedSection) return;
    const sectionToMove: LegalDocumentSection = draggedSection;

    const insertRelative = (sections: LegalDocumentSection[], parentSection: LegalDocumentSection | null): boolean => {
      const targetIdx = sections.findIndex((s) => s.id === targetId);
      if (targetIdx !== -1) {
        const insertIdx = position === 'before' ? targetIdx : targetIdx + 1;
        draggedSection!.parent = parentSection ?? undefined;
        sections.splice(insertIdx, 0, draggedSection!);
        sections.forEach((s, i) => { s.orderIndex = i + 1; });
        return true;
      }
      return sections.some((s) => insertRelative(s.children, s));
    };
    const inserted = insertRelative(legalDocument.rootSections, null);
    if (!inserted) {
      // Filet de sécurité : si la cible est introuvable, on remet la section
      // à la racine pour ne jamais la perdre.
      sectionToMove.parent = undefined;
      sectionToMove.orderIndex = legalDocument.rootSections.length + 1;
      legalDocument.rootSections.push(sectionToMove);
    }

    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };

  const findSectionById = (
    sectionId: string,
    documents: LegalDocument[]
  ): LegalDocumentSection | null => {
    for (const doc of documents) {
      const allSections = getAllSectionsFromDocument(doc);
      const section = allSections.find((s) => s.id === sectionId);
      if (section) return section;
    }
    return null;
  };

  const addReference = (sectionId: string, referencedSectionId: string) => {
    if (!legalDocument) return;

    const findAndUpdateSection = (
      sections: LegalDocumentSection[]
    ): boolean => {
      for (const section of sections) {
        if (section.id === sectionId) {
          // Chercher la section référencée dans tous les documents disponibles
          const referencedSection = findSectionById(
            referencedSectionId,
            searchResults
          );
          if (
            referencedSection &&
            !section.references.includes(referencedSection)
          ) {
            section.references.push(referencedSection);
            // Mettre à jour referencedBy seulement si c'est dans le même document
            if (referencedSection.document.id === legalDocument.id) {
              if (!referencedSection.referencedBy.includes(section)) {
                referencedSection.referencedBy.push(section);
              }
            }
          }
          return true;
        }
        if (findAndUpdateSection(section.children)) return true;
      }
      return false;
    };

    findAndUpdateSection(legalDocument.rootSections);
    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };

  const removeReference = (sectionId: string, referencedSectionId: string) => {
    if (!legalDocument) return;

    const findAndUpdateSection = (
      sections: LegalDocumentSection[]
    ): boolean => {
      for (const section of sections) {
        if (section.id === sectionId) {
          const refIndex = section.references.findIndex(
            (ref) => ref.id === referencedSectionId
          );
          if (refIndex !== -1) {
            const referencedSection = section.references[refIndex];
            section.references.splice(refIndex, 1);
            // Mettre à jour referencedBy seulement si c'est dans le même document
            if (referencedSection.document.id === legalDocument.id) {
              const backRefIndex = referencedSection.referencedBy.findIndex(
                (s) => s.id === sectionId
              );
              if (backRefIndex !== -1) {
                referencedSection.referencedBy.splice(backRefIndex, 1);
              }
            }
          }
          return true;
        }
        if (findAndUpdateSection(section.children)) return true;
      }
      return false;
    };

    findAndUpdateSection(legalDocument.rootSections);
    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };

  // Aplatit le sous-arbre d'une section : conserve sa PREMIÈRE sous-section comme
  // unique enfant et y regroupe, dans un UNIQUE chunk, le contenu de toutes les
  // autres sections descendantes (parcours préfixe). Les sections fusionnées sont
  // ensuite supprimées. Les références sortantes des sections fusionnées sont
  // additionnées dans la première sous-section (sans doublon) et les références
  // entrantes pointant vers une section supprimée y sont redirigées.
  const mergeSubsectionsIntoFirst = (sectionId: string) => {
    if (!legalDocument) return;

    const allSections = getAllSections(legalDocument.rootSections);
    const parentSection = allSections.find((s) => s.id === sectionId);
    if (!parentSection || parentSection.children.length === 0) return;

    // Descendants en parcours préfixe (profondeur d'abord) pour conserver
    // l'ordre de lecture du document.
    const descendants: LegalDocumentSection[] = [];
    const collectPreorder = (sections: LegalDocumentSection[]) => {
      sections.forEach((s) => {
        descendants.push(s);
        collectPreorder(s.children);
      });
    };
    collectPreorder(parentSection.children);

    // Rien à fusionner s'il n'existe qu'une seule sous-section feuille.
    if (descendants.length <= 1) return;

    // La première sous-section devient le conteneur ; toutes les autres sections
    // descendantes y sont fusionnées puis supprimées.
    const container = descendants[0];
    const mergedSections = descendants.slice(1);
    const removedIds = new Set(mergedSections.map((s) => s.id));

    // 1. Regrouper tout le contenu (conteneur + sections fusionnées, en parcours
    //    préfixe) dans un UNIQUE chunk du conteneur. On réutilise le premier chunk
    //    existant (mise à jour de son contenu) et on supprime les autres ; les
    //    chunks des sections fusionnées disparaissent en cascade avec elles.
    const mergedContent = descendants
      .flatMap((s) => s.contentChunks.map((chunk) => chunk.content))
      .filter((content) => content != null && content.trim() !== "")
      .join("");
    if (container.contentChunks.length > 0) {
      const firstChunk = container.contentChunks[0];
      firstChunk.content = mergedContent;
      firstChunk.position = 0;
      container.contentChunks = [firstChunk];
    } else {
      container.contentChunks = [
        {
          id: generateId(),
          position: 0,
          content: mergedContent,
          embedding: "",
          section: container,
        },
      ];
    }

    // 2. Additionner les références sortantes (conteneur + sections fusionnées)
    //    sans doublon, en excluant les auto-références et les références internes
    //    au sous-arbre supprimé.
    const combinedReferences: LegalDocumentSection[] = [];
    const seenReferenceIds = new Set<string>();
    descendants.forEach((s) => {
      s.references.forEach((ref) => {
        if (ref.id === container.id || removedIds.has(ref.id)) return;
        if (seenReferenceIds.has(ref.id)) return;
        seenReferenceIds.add(ref.id);
        combinedReferences.push(ref);
      });
    });
    container.references = combinedReferences;

    // 3. Rediriger vers le conteneur les références entrantes qui pointaient vers
    //    une section supprimée, sans doublon, afin de ne pas perdre les
    //    cross-références existantes.
    allSections.forEach((s) => {
      if (s.id === container.id || removedIds.has(s.id)) return;
      if (!s.references.some((ref) => removedIds.has(ref.id))) return;
      s.references = s.references.filter((ref) => !removedIds.has(ref.id));
      if (!s.references.some((ref) => ref.id === container.id)) {
        s.references.push(container);
      }
    });

    // 4. Le conteneur devient une feuille et l'unique enfant de la section.
    container.children = [];
    parentSection.children = [container];

    setLegalDocument({ ...legalDocument });
    markAsDirty();
  };

  const renderTocItem = (section: LegalDocumentSection, level = 0) => {
    const hasChildren = section.children.length > 0;
    const isExpanded = expandedTocItems.has(section.id);

    // Ne pas afficher les sections sans enfants (sections feuilles)
    if (!hasChildren) {
      return null;
    }

    // Vérifier si cette section a des enfants qui seront affichés (qui ont eux-mêmes des enfants)
    const hasDisplayableChildren = section.children.some(
      (child) => child.children.length > 0
    );

    return (
      <div key={section.id} className="select-none">
        <div
          style={{
            marginLeft: `${level}rem`,
          }}
          className={cn(
            "flex items-center gap-1 py-1 px-2 rounded text-sm cursor-pointer transition-colors duration-150 text-foreground",
            "hover:bg-muted hover:text-primary"
          )}
          onClick={() => navigateToSection(section.id)}
        >
          {hasDisplayableChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleTocItem(section.id);
              }}
              className="p-0.5 hover:bg-muted rounded transition-colors duration-150"
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
          )}
          {!hasDisplayableChildren && <div className="w-4" />}
          <span className="truncate flex-1">
            {section.contentChunks.map((chunk) => stripHtml(chunk.content)).join(" ")}
          </span>
        </div>
        {hasDisplayableChildren && isExpanded && (
          <div>
            {section.children.map((child) => renderTocItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderCrossReference = (referencedSection: LegalDocumentSection) => {
    const isExternalReference =
      legalDocument && referencedSection.document.id !== legalDocument.id;

    return (
      <button
        key={referencedSection.id}
        onClick={() => {
          if (isExternalReference) {
            const url = `/feature/legalDocuments/pdf-viewer/${referencedSection.document.id}?section=${encodeURIComponent(referencedSection.id)}`;
            window.open(url, "_blank", "noopener,noreferrer");
          } else {
            navigateToSection(referencedSection.id);
          }
        }}
        className={`inline-flex items-center gap-1 text-xs hover:underline ml-2 ${
          isExternalReference
            ? "text-purple-600 hover:text-purple-800"
            : "text-blue-600 hover:text-blue-800"
        }`}
      >
        <ExternalLink className="h-3 w-3" />
        {isExternalReference && (
          <span className="font-medium">
            [{referencedSection.document.title}]
          </span>
        )}
        {referencedSection.contentChunks
          .map((chunk) => stripHtml(chunk.content))
          .join(" ")}
      </button>
    );
  };

  // Fonction réutilisable pour rendre les boutons d'actions
  const renderActionButtons = (
    section: LegalDocumentSection,
    chunk: LegalDocumentSectionChunk
  ) => (
    <div className="absolute -right-2 top-0 flex gap-1 bg-card border border-border rounded shadow-sm p-1 z-20">
      <button
        onClick={() => {
          setNewSectionParent(section.id);
          setNewSectionContent("");
        }}
        className="p-1 hover:bg-green-100 rounded"
        title="Créer une section enfant"
      >
        <Plus className="h-3 w-3 text-green-600" />
      </button>
      <button
        onClick={() => {
          setEditingChunk(chunk.id);
          setChunkContent(chunk.content);
        }}
        className="p-1 hover:bg-muted rounded"
        title="Modifier le chunk"
      >
        <Edit className="h-3 w-3" />
      </button>
      {(section.children.length > 1 ||
        (section.children.length === 1 &&
          section.children[0].children.length > 0)) && (
        <button
          onClick={() => mergeSubsectionsIntoFirst(section.id)}
          className="p-1 hover:bg-amber-100 rounded"
          title="Fusionner le contenu des sous-sections dans la première sous-section"
        >
          <Combine className="h-3 w-3 text-amber-600" />
        </button>
      )}
      <button
        onClick={() => setShowDeleteConfirm(section.id)}
        className="p-1 hover:bg-red-100 rounded"
        title="Supprimer la section"
      >
        <Trash2 className="h-3 w-3 text-red-600" />
      </button>
      <div className="border-l border-gray-300 mx-1"></div>
      <button
        onClick={() => {
          setShowNewChunkInput(section.id);
          setNewChunkInputContent("");
        }}
        className="p-1 hover:bg-blue-100 rounded"
        title="Ajouter un chunk à la section"
      >
        <Plus className="h-3 w-3 text-blue-600" />
      </button>
      <button
        onClick={() => setShowDeleteChunkConfirm(chunk.id)}
        disabled={section.contentChunks.length === 1}
        className={`p-1 rounded ${
          section.contentChunks.length === 1
            ? "cursor-not-allowed opacity-50"
            : "hover:bg-red-100"
        }`}
        title={
          section.contentChunks.length === 1
            ? "Impossible de supprimer le seul chunk de la section"
            : "Supprimer le chunk"
        }
      >
        <Trash2 className="h-3 w-3 text-red-600" />
      </button>
      <div className="border-l border-gray-300 mx-1"></div>
      <button
        onClick={() => {
          setManagingReferences(section.id);
          setAvailableDocuments(searchResults);
          setSelectedDocumentForReference("");
          setAvailableSections([]);
        }}
        className="p-1 hover:bg-purple-100 rounded"
        title="Gérer les références"
      >
        <Link className="h-3 w-3 text-purple-600" />
      </button>
    </div>
  );

  // Strip HTML tags from text and collapse block whitespace into a single readable label.
  const stripHtml = (html: string): string => {
    if (!html) return "";
    const withSeparators = html
      .replace(/<\s*br\s*\/?\s*>/gi, " ")
      .replace(/<\/\s*(p|div|li|h[1-6]|tr|td|th|ul|ol)\s*>/gi, " ");
    const tmp = document.createElement("DIV");
    tmp.innerHTML = withSeparators;
    return (tmp.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  // Fonction réutilisable pour rendre le contenu des chunks
  const renderChunksContent = (
    section: LegalDocumentSection,
    hasReferences: boolean,
    useInlineDisplay = false
  ) => {
    if (isEditMode) {
      // Mode édition avec dividers discrets
      return (
        <div className="space-y-1">
          {section.contentChunks.map((chunk, index) => (
            <div key={chunk.id}>
              {editingChunk === chunk.id ? (
                <div className="space-y-2 p-2 border rounded bg-blue-50">
                  <textarea
                    value={chunkContent}
                    onChange={(e) => setChunkContent(e.target.value)}
                    className="w-full p-2 border border-border rounded text-sm bg-background text-foreground"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateChunk(chunk.id, chunkContent)}
                      className="px-2 py-1 bg-green-600 text-white rounded text-xs flex items-center gap-1"
                    >
                      <Save className="h-3 w-3" />
                      Sauvegarder
                    </button>
                    <button
                      onClick={() => {
                        setEditingChunk(null);
                        setChunkContent("");
                      }}
                      className="px-2 py-1 bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                    >
                      <X className="h-3 w-3" />
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={
                    "relative group/chunk" +
                    (hoveredChunk === chunk.id
                      ? " bg-muted p-[6px] -m-[6px] rounded-lg"
                      : "")
                  }
                  onMouseEnter={() => setHoveredChunk(chunk.id)}
                  onMouseLeave={() => setHoveredChunk(null)}
                >
                  {useInlineDisplay ? (
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap">
                      {chunk.content}
                      {hasReferences && (
                        <span className="inline-block">
                          {section.references.map(renderCrossReference)}
                        </span>
                      )}
                    </p>
                  ) : (
                    <span>{chunk.content}</span>
                  )}
                  {hoveredChunk === chunk.id &&
                    renderActionButtons(section, chunk)}
                </div>
              )}
              {index < section.contentChunks.length - 1 && (
                <div className="border-b border-border my-1 opacity-30"></div>
              )}
            </div>
          ))}
        </div>
      );
    } else {
      // Mode normal
      if (useInlineDisplay) {
        return section.contentChunks.map((chunk) => (
          <p key={chunk.id} className="text-foreground leading-relaxed whitespace-pre-wrap">
                      <div
                        className="ai-response"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(chunk.content) }}
                      />
            {hasReferences && (
              <span className="inline-block">
                {section.references.map(renderCrossReference)}
              </span>
            )}
          </p>
        ));
      } else {
        return section.contentChunks.map((chunk) => stripHtml(chunk.content)).join(" ");
      }
    }
  };

  const renderSection = (section: LegalDocumentSection, level = 0) => {
    const isExpanded = expandedSections.has(section.id);
    const hasChildren = section.children.length > 0;
    const hasContent = section.contentChunks.length > 0;
    const hasReferences = section.references.length > 0;
    const isBeingDragged = isEditMode && draggedSectionId === section.id;
    const isDropTarget = isEditMode && dropTargetSectionId === section.id;

    return (
      <div
        key={section.id}
        id={`section-${section.id}`}
        className={cn(
          "mb-6 rounded-lg transition-all",
          isBeingDragged && "opacity-40"
        )}
        onDragLeave={isEditMode ? (e) => {
          if (!(e.currentTarget as Element).contains(e.relatedTarget as Node)) {
            setDropTargetSectionId((prev) => prev === section.id ? null : prev);
            setDropPosition(null);
          }
        } : undefined}
      >
        <div
          style={{
            marginLeft: `${level}rem`,
          }}
          className={isEditMode || !isAdmin ? "select-none" : undefined}
        >
          {/* Zone de dépôt AVANT (= frère avant cette section) */}
          {isEditMode && (
            <div
              className={cn(
                "h-1.5 rounded-full transition-colors mb-1 -mt-1",
                isDropTarget && dropPosition === 'before' ? "bg-blue-400" : "bg-transparent hover:bg-blue-100"
              )}
              onDragOver={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (draggedSectionId && draggedSectionId !== section.id && !isDescendantOrSelf(section.id, draggedSectionId)) {
                  setDropTargetSectionId(section.id);
                  setDropPosition('before');
                  e.dataTransfer.dropEffect = 'move';
                }
              }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (draggedSectionId && draggedSectionId !== section.id && !isDescendantOrSelf(section.id, draggedSectionId)) {
                  moveSectionRelativeToSibling(draggedSectionId, section.id, 'before');
                }
                setDraggedSectionId(null); setDropTargetSectionId(null); setDropPosition(null);
              }}
            />
          )}

          {/* Header = zone de dépôt ENFANT (drop ici → devient enfant de cette section) */}
          <div
            className={cn(
              "flex items-center gap-2 mb-3 relative group",
              isEditMode && isDropTarget && dropPosition === 'child' && "ring-2 ring-blue-400 ring-offset-1 rounded bg-blue-50/30 dark:bg-blue-900/20"
            )}
            onDragOver={isEditMode ? (e) => {
              e.preventDefault(); e.stopPropagation();
              if (draggedSectionId && draggedSectionId !== section.id && !isDescendantOrSelf(section.id, draggedSectionId)) {
                setDropTargetSectionId(section.id);
                setDropPosition('child');
                e.dataTransfer.dropEffect = 'move';
              }
            } : undefined}
            onDrop={isEditMode ? (e) => {
              e.preventDefault(); e.stopPropagation();
              if (draggedSectionId && draggedSectionId !== section.id && !isDescendantOrSelf(section.id, draggedSectionId)) {
                moveSectionToNewParent(draggedSectionId, section.id);
              }
              setDraggedSectionId(null); setDropTargetSectionId(null); setDropPosition(null);
            } : undefined}
          >
            {isEditMode && (
              <div
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  setDraggedSectionId(section.id);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", section.id);
                }}
                onDragEnd={() => {
                  setDraggedSectionId(null);
                  setDropTargetSectionId(null);
                  setDropPosition(null);
                }}
                className="cursor-grab active:cursor-grabbing shrink-0 p-0.5 rounded hover:bg-muted"
                title="Déplacer la section"
              >
                <GripVertical className="h-4 w-4 text-muted-foreground opacity-40 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
            {hasChildren && (
              <button
                onClick={() => toggleSection(section.id)}
                className="p-1 hover:bg-accent rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            )}

            {hasChildren && (
              <h2
                className={cn(
                  "font-semibold flex-1",
                  level === 0 && "text-2xl",
                  level === 1 && "text-xl",
                  level === 2 && "text-lg",
                  level >= 3 && "text-base"
                )}
              >
                {renderChunksContent(section, hasReferences, false)}
              </h2>
            )}

            {!hasChildren && (
              <div className="flex-1">
                {renderChunksContent(section, hasReferences, true)}
              </div>
            )}
          </div>

          {/* Formulaire pour ajouter un nouveau chunk */}
          {isEditMode && showNewChunkInput === section.id && (
            <div className="mb-4 p-4 border-2 border-dashed border-blue-300 rounded bg-blue-50">
              <h4 className="font-medium mb-2">Ajouter un nouveau chunk</h4>
              <textarea
                value={newChunkInputContent}
                onChange={(e) => setNewChunkInputContent(e.target.value)}
                placeholder="Contenu du nouveau chunk..."
                className="w-full p-2 border border-border rounded text-sm mb-2 bg-background text-foreground"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (newChunkInputContent.trim()) {
                      addChunkToSection(
                        showNewChunkInput,
                        newChunkInputContent.trim()
                      );
                      setShowNewChunkInput(null);
                      setNewChunkInputContent("");
                    }
                  }}
                  className="px-3 py-1 bg-primary text-primary-foreground rounded text-xs flex items-center gap-1"
                >
                  <Save className="h-3 w-3" />
                  Ajouter
                </button>
                <button
                  onClick={() => {
                    setShowNewChunkInput(null);
                    setNewChunkInputContent("");
                  }}
                  className="px-3 py-1 bg-muted text-muted-foreground rounded text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* Confirmation de suppression */}
          {isEditMode && showDeleteConfirm === section.id && (
            <div className="mb-4 p-4 border-2 border-red-300 rounded bg-red-50">
              <h4 className="font-medium mb-2 text-red-800">
                Confirmer la suppression
              </h4>
              <p className="text-sm text-red-700 mb-3">
                Êtes-vous sûr de vouloir supprimer cette section ? Cette action
                est irréversible.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    deleteSection(showDeleteConfirm);
                    setShowDeleteConfirm(null);
                  }}
                  className="px-3 py-1 bg-red-600 text-white rounded text-xs flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Supprimer
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* Confirmation de suppression de chunk */}
          {showDeleteChunkConfirm &&
            section.contentChunks.some(
              (chunk) => chunk.id === showDeleteChunkConfirm
            ) && (
              <div className="mb-4 p-4 border-2 border-red-300 rounded bg-red-50">
                <h4 className="font-medium mb-2 text-red-800">
                  Confirmer la suppression du chunk
                </h4>
                <p className="text-sm text-red-700 mb-3">
                  Êtes-vous sûr de vouloir supprimer ce chunk ? Cette action est
                  irréversible.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      deleteChunk(showDeleteChunkConfirm);
                      setShowDeleteChunkConfirm(null);
                    }}
                    className="px-3 py-1 bg-red-600 text-white rounded text-xs flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Supprimer
                  </button>
                  <button
                    onClick={() => setShowDeleteChunkConfirm(null)}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                  >
                    <X className="h-3 w-3" />
                    Annuler
                  </button>
                </div>
              </div>
            )}

          {/* Formulaire pour créer une nouvelle section enfant */}
          {isEditMode && newSectionParent === section.id && (
            <div className="mb-4 p-4 border-2 border-dashed border-blue-300 rounded bg-blue-50">
              <h4 className="font-medium mb-2">
                Créer une nouvelle section enfant
              </h4>
              <textarea
                value={newSectionContent}
                onChange={(e) => setNewSectionContent(e.target.value)}
                placeholder="Contenu de la nouvelle section..."
                className="w-full p-2 border rounded text-sm mb-2"
                rows={3}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (newSectionContent.trim()) {
                      createNewSection(section.id, newSectionContent.trim());
                    }
                  }}
                  className="px-3 py-1 bg-green-600 text-white rounded text-xs flex items-center gap-1"
                >
                  <Save className="h-3 w-3" />
                  Créer
                </button>
                <button
                  onClick={() => {
                    setNewSectionParent(null);
                    setNewSectionContent("");
                  }}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                >
                  <X className="h-3 w-3" />
                  Annuler
                </button>
              </div>
            </div>
          )}

          {/* Interface de gestion des références */}
          {isEditMode && managingReferences === section.id && (
            <div className="mb-4 p-4 border-2 border-purple-300 rounded bg-purple-50">
              <h4 className="font-medium mb-3 text-purple-800 flex items-center gap-2">
                <Link className="h-4 w-4" />
                Gérer les références
              </h4>

              {/* Références actuelles */}
              {section.references.length > 0 && (
                <div className="mb-4">
                  <h5 className="text-sm font-medium mb-2 text-purple-700">
                    Références actuelles :
                  </h5>
                  <div className="space-y-2">
                    {section.references.map((ref) => (
                      <div
                        key={ref.id}
                        className="flex items-center justify-between bg-white p-2 rounded border"
                      >
                        <div className="text-sm flex-1">
                          {ref.document.id !== legalDocument?.id && (
                            <span className="font-medium text-purple-600 block">
                              [{ref.document.title}]
                            </span>
                          )}
                          <span>
                            {ref.contentChunks
                              .map((chunk) => stripHtml(chunk.content))
                              .join(" ")}
                          </span>
                        </div>
                        <button
                          onClick={() => removeReference(section.id, ref.id)}
                          className="p-1 hover:bg-red-100 rounded ml-2"
                          title="Supprimer cette référence"
                        >
                          <X className="h-3 w-3 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ajouter une nouvelle référence */}
              <div>
                <h5 className="text-sm font-medium mb-2 text-purple-700">
                  Ajouter une référence :
                </h5>

                {/* Sélection du document avec autocomplete */}
                <div className="relative mb-2 search-dropdown-container">
                  <input
                    type="text"
                    value={documentSearchQuery}
                    onChange={(e) => {
                      setDocumentSearchQuery(e.target.value);
                      setShowSearchDropdown(true);
                    }}
                    onFocus={() => setShowSearchDropdown(true)}
                    placeholder="Rechercher un document..."
                    className="w-full p-2 border border-border rounded text-sm pr-8 bg-background text-foreground"
                    autoComplete="off"
                  />
                  {isSearching && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    </div>
                  )}

                  {/* Dropdown avec résultats */}
                  {showSearchDropdown &&
                    (documentSearchQuery || searchResults.length > 0) && (
                      <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded shadow-lg max-h-60 overflow-y-auto">
                        {searchResults.length === 0 && !isSearching ? (
                          <div className="p-3 text-sm text-muted-foreground text-center">
                            Aucun document trouvé
                          </div>
                        ) : (
                          <>
                            {searchResults.map((doc) => (
                              <div
                                key={doc.id}
                                onClick={() => {
                                  setSelectedDocumentForReference(doc.id);
                                  setDocumentSearchQuery(doc.title);
                                  setShowSearchDropdown(false);
                                  setNewReferenceInput("");
                                  const allSectionsFromDoc =
                                    getAllSectionsFromDocument(doc);
                                  setAvailableSections(
                                    allSectionsFromDoc.filter(
                                      (s) => s.id !== section.id
                                    )
                                  );
                                }}
                                className={`p-2 cursor-pointer hover:bg-muted border-b border-border last:border-b-0 ${
                                  selectedDocumentForReference === doc.id
                                    ? "bg-muted"
                                    : ""
                                }`}
                              >
                                <div className="text-sm font-medium">
                                    {stripHtml(doc.title)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {doc.authors} • {doc.type}
                                  {doc.id === legalDocument?.id
                                    ? " (Document actuel)"
                                    : ""}
                                </div>
                              </div>
                            ))}

                            {/* Bouton pour charger plus */}
                            {hasMoreResults && (
                              <div className="p-2 border-t">
                                <button
                                  onClick={loadMoreResults}
                                  disabled={isLoadingMore}
                                  className="w-full p-2 text-sm text-purple-600 hover:bg-purple-50 rounded flex items-center justify-center gap-2"
                                >
                                  {isLoadingMore ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600"></div>
                                      Chargement...
                                    </>
                                  ) : (
                                    "Charger plus de résultats"
                                  )}
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                </div>

                {/* Sélection de la section */}
                <select
                  value={newReferenceInput}
                  onChange={(e) => setNewReferenceInput(e.target.value)}
                  className="w-full p-2 border rounded text-sm mb-2"
                  disabled={!selectedDocumentForReference}
                >
                  <option value="">
                    {selectedDocumentForReference
                      ? "Sélectionner une section..."
                      : "Sélectionnez d'abord un document"}
                  </option>
                  {availableSections
                    .filter(
                      (s) => !section.references.some((ref) => ref.id === s.id)
                    )
                    .map((availableSection) => (
                      <option
                        key={availableSection.id}
                        value={availableSection.id}
                      >
                        {availableSection.contentChunks
                          .map((chunk) => stripHtml(chunk.content))
                          .join(" ")
                          .substring(0, 80)}
                        {availableSection.contentChunks
                          .map((chunk) => stripHtml(chunk.content))
                          .join(" ").length > 80
                          ? "..."
                          : ""}
                      </option>
                    ))}
                </select>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (newReferenceInput) {
                        addReference(section.id, newReferenceInput);
                        setNewReferenceInput("");
                      }
                    }}
                    disabled={!newReferenceInput}
                    className="px-3 py-1 bg-purple-600 text-white rounded text-xs flex items-center gap-1 disabled:opacity-50"
                  >
                    <Plus className="h-3 w-3" />
                    Ajouter
                  </button>
                  <button
                    onClick={() => {
                      setManagingReferences(null);
                      setNewReferenceInput("");
                      setAvailableSections([]);
                      setAvailableDocuments([]);
                      setSelectedDocumentForReference("");
                      setDocumentSearchQuery("");
                      setSearchResults([]);
                      setShowSearchDropdown(false);
                    }}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                  >
                    <X className="h-3 w-3" />
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          )}
        {/* Zone de dépôt APRÈS (= frère après cette section) */}
        {isEditMode && (
          <div
            className={cn(
              "h-1.5 rounded-full transition-colors mt-1 -mb-1",
              isDropTarget && dropPosition === 'after' ? "bg-blue-400" : "bg-transparent hover:bg-blue-100"
            )}
            onDragOver={(e) => {
              e.preventDefault(); e.stopPropagation();
              if (draggedSectionId && draggedSectionId !== section.id && !isDescendantOrSelf(section.id, draggedSectionId)) {
                setDropTargetSectionId(section.id);
                setDropPosition('after');
                e.dataTransfer.dropEffect = 'move';
              }
            }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation();
              if (draggedSectionId && draggedSectionId !== section.id && !isDescendantOrSelf(section.id, draggedSectionId)) {
                moveSectionRelativeToSibling(draggedSectionId, section.id, 'after');
              }
              setDraggedSectionId(null); setDropTargetSectionId(null); setDropPosition(null);
            }}
          />
        )}
        </div>

        {/* Pour les sections avec enfants, afficher les enfants seulement quand développé */}
        {hasChildren && isExpanded && (
          <div>
            {section.children.map((child) => renderSection(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full w-full bg-background text-foreground">
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement du document...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-destructive">
            <p className="text-lg font-semibold mb-2">Erreur</p>
            <p>{error}</p>
          </div>
        </div>
      )}

      {!loading && !error && !legalDocument && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <p>Document non trouvé</p>
          </div>
        </div>
      )}

      {!loading && !error && legalDocument && (
        <>
          {/* Sidebar - Table of Contents */}
          <div
            className={cn(
              "border-r border-border bg-card overflow-y-auto transition-all duration-300 ease-in-out",
              isSidebarCollapsed ? "w-0" : "w-80"
            )}
          >
            {!isSidebarCollapsed && (
              <div className="p-4">
                <div className="mb-4">
                  <h1 className="font-bold text-lg text-foreground mb-2">
                    Table des matières
                  </h1>
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium">{legalDocument.title}</p>
                    <p className="mt-0">Version {legalDocument.version}</p>
                    <p className="mt-0">Par {legalDocument.authors?.join(", ")}</p>
                  </div>
                </div>

                <div className="space-y-1">
                  {legalDocument.rootSections.map((section) =>
                    renderTocItem(section)
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 p-8 overflow-y-auto bg-background relative pt-3">
            {/* Toggle Sidebar Button */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="p-2 bg-card border border-border rounded-lg shadow-sm hover:bg-muted transition-colors duration-200"
                  title={
                    isSidebarCollapsed
                      ? "Afficher la table des matières"
                      : "Masquer la table des matières"
                  }
                >
                  <TableOfContents className="h-4 w-4 text-muted-foreground" />
                </button>
                {legalDocument && (() => {
                  const allIds = getAllSections(legalDocument.rootSections)
                    .filter((s) => s.children.length > 0)
                    .map((s) => s.id);
                  const allExpanded = allIds.length > 0 && allIds.every((id) => expandedSections.has(id));
                  return (
                    <button
                      onClick={toggleAllSections}
                      className="p-2 bg-card border border-border rounded-lg shadow-sm hover:bg-muted transition-colors duration-200"
                      title={allExpanded ? "Réduire toutes les sections" : "Développer toutes les sections"}
                    >
                      {allExpanded
                        ? <ChevronsDownUp className="h-4 w-4 text-muted-foreground" />
                        : <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />}
                    </button>
                  );
                })()}
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <button
                    onClick={handleRecomputeCrossReferences}
                    disabled={isSaving || isRecomputingCrossRefs || isEditMode}
                    className="px-4 py-2 bg-blue-600 text-white rounded flex items-center gap-2 font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      isEditMode
                        ? "Quittez le mode édition pour recalculer"
                        : "Recalculer les cross-références"
                    }
                  >
                    <Link className="h-4 w-4" />
                    {isRecomputingCrossRefs
                      ? "Recalcul en cours..."
                      : "Recalculer cross-références"}
                  </button>
                  {isEditMode && isDirty && (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="px-4 py-2 bg-green-600 text-white rounded flex items-center gap-2 font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Save className="h-4 w-4" />
                        {isSaving ? "Sauvegarde..." : "Sauvegarder"}
                      </button>
                      <button
                        onClick={handleDiscardChanges}
                        disabled={isSaving}
                        className="px-4 py-2 bg-orange-600 text-white rounded flex items-center gap-2 font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Annuler
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      if (
                        isDirty &&
                        !window.confirm(
                          "Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter le mode édition ?"
                        )
                      ) {
                        return;
                      }
                      setIsEditMode(!isEditMode);
                      // Réinitialiser les états d'édition
                      setEditingChunk(null);
                      setChunkContent("");
                      setNewSectionParent(null);
                      setNewSectionContent("");
                      setHoveredChunk(null);
                      setShowDeleteConfirm(null);
                      setShowDeleteChunkConfirm(null);
                      setShowNewChunkInput(null);
                      setNewChunkInputContent("");
                      setShowNewRootSectionInput(false);
                      setNewRootSectionContent("");
                      setManagingReferences(null);
                      setNewReferenceInput("");
                      setAvailableSections([]);
                      setAvailableDocuments([]);
                      setSelectedDocumentForReference("");
                    }}
                    className={cn(
                      "px-4 py-2 rounded flex items-center gap-2 font-medium",
                      isEditMode
                        ? "bg-muted text-foreground hover:bg-muted/80"
                        : "bg-primary text-primary-foreground hover:bg-primary/80"
                    )}
                  >
                    <Edit className="h-4 w-4" />
                    {isEditMode ? "Mode lecture" : "Modifier"}
                  </button>
                </div>
              )}
            </div>

            <header className="mb-8">
              <div className="flex justify-between items-start mb-4">
                <h1 className="text-3xl font-bold text-foreground">
                  {legalDocument.title}
                </h1>
              </div>

              {/* Notification de modifications non sauvegardées */}
              {isEditMode && isDirty && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-800 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    <strong>Modifications non sauvegardées</strong> - N'oubliez
                    pas de sauvegarder vos changements
                  </p>
                </div>
              )}

              {isEditMode && !isDirty && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800">
                    <strong>Mode édition activé :</strong> Vous pouvez
                    maintenant modifier le contenu des sections, ajouter ou
                    supprimer des chunks, et créer de nouvelles sections.
                  </p>
                </div>
              )}

              <div className="text-muted-foreground space-y-1">
                {isEditMode ? (
                  <div className="border rounded-lg p-4 space-y-3 bg-muted/20 text-foreground">
                    <p className="text-sm font-semibold">Métadonnées du document</p>

                    {/* Titre */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Titre</label>
                      <input
                        className="w-full p-2 border border-border rounded text-sm bg-background text-foreground"
                        value={legalDocument.title}
                        onChange={(e) => { setLegalDocument({ ...legalDocument, title: e.target.value }); markAsDirty(); }}
                      />
                    </div>

                    {/* Type + Catégorie */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Type</label>
                        <select
                          className="w-full p-2 border border-border rounded text-sm bg-background text-foreground h-9"
                          value={legalDocument.type ?? ""}
                          onChange={(e) => { setLegalDocument({ ...legalDocument, type: e.target.value }); markAsDirty(); }}
                        >
                          <option value="">—</option>
                          {LEGAL_TYPES.map((ty) => (
                            <option key={ty.value} value={ty.value}>
                              {t(ty.labelKey, { defaultValue: ty.value })}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Catégorie</label>
                        <select
                          className="w-full p-2 border border-border rounded text-sm bg-background text-foreground h-9"
                          value={legalDocument.category ?? ""}
                          onChange={(e) => { setLegalDocument({ ...legalDocument, category: e.target.value }); markAsDirty(); }}
                        >
                          <option value="">—</option>
                          {LEGAL_CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>
                              {t(c.labelKey, { defaultValue: c.value })}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Auteurs */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Auteurs</label>
                      <div className="flex gap-2">
                        <input
                          className="flex-1 p-2 border border-border rounded text-sm bg-background text-foreground h-9"
                          placeholder="Ajouter un auteur..."
                          value={metadataAuthorInput}
                          onChange={(e) => setMetadataAuthorInput(e.target.value)}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === ",") && metadataAuthorInput.trim()) {
                              e.preventDefault();
                              const val = metadataAuthorInput.trim();
                              if (!legalDocument.authors.includes(val)) {
                                setLegalDocument({ ...legalDocument, authors: [...legalDocument.authors, val] });
                                markAsDirty();
                              }
                              setMetadataAuthorInput("");
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="px-3 h-9 border border-border rounded text-sm hover:bg-muted"
                          onClick={() => {
                            const val = metadataAuthorInput.trim();
                            if (val && !legalDocument.authors.includes(val)) {
                              setLegalDocument({ ...legalDocument, authors: [...legalDocument.authors, val] });
                              markAsDirty();
                            }
                            setMetadataAuthorInput("");
                          }}
                        >
                          Ajouter
                        </button>
                      </div>
                      {legalDocument.authors?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {legalDocument.authors.map((a) => (
                            <span key={a} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full px-2.5 py-0.5">
                              {a}
                              <button
                                onClick={() => {
                                  setLegalDocument({ ...legalDocument, authors: legalDocument.authors.filter((x) => x !== a) });
                                  markAsDirty();
                                }}
                                className="hover:text-destructive"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Version + Valide du + Valide au */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Version</label>
                        <input
                          className="w-full p-2 border border-border rounded text-sm bg-background text-foreground h-9"
                          value={legalDocument.version ?? ""}
                          onChange={(e) => { setLegalDocument({ ...legalDocument, version: e.target.value }); markAsDirty(); }}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Valide du</label>
                        <input
                          type="date"
                          className="w-full p-2 border border-border rounded text-sm bg-background text-foreground h-9"
                          value={legalDocument.validFrom ? dayjs(legalDocument.validFrom).format("YYYY-MM-DD") : ""}
                          onChange={(e) => { setLegalDocument({ ...legalDocument, validFrom: e.target.value ? dayjs(e.target.value) : null }); markAsDirty(); }}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">Valide au</label>
                        <input
                          type="date"
                          className="w-full p-2 border border-border rounded text-sm bg-background text-foreground h-9"
                          value={legalDocument.validUntil ? dayjs(legalDocument.validUntil).format("YYYY-MM-DD") : ""}
                          onChange={(e) => { setLegalDocument({ ...legalDocument, validUntil: e.target.value ? dayjs(e.target.value) : null }); markAsDirty(); }}
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">Description</label>
                      <textarea
                        className="w-full p-2 border border-border rounded text-sm bg-background text-foreground"
                        rows={3}
                        value={legalDocument.description ?? ""}
                        onChange={(e) => { setLegalDocument({ ...legalDocument, description: e.target.value }); markAsDirty(); }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <p>
                      <strong>Auteurs:</strong>{" "}
                      {legalDocument.authors?.length ? legalDocument.authors.join(", ") : "—"}
                    </p>
                    <p>
                      <strong>Type:</strong>{" "}
                      {legalDocument.type ? t(getTypeLabelKey(legalDocument.type), { defaultValue: legalDocument.type }) : ""}{legalDocument.type && legalDocument.category ? " - " : ""}
                      {legalDocument.category ? t(getCategoryLabelKey(legalDocument.category), { defaultValue: legalDocument.category }) : ""}
                    </p>
                    <p>
                      <strong>Version:</strong> {legalDocument.version}
                    </p>
                    {legalDocument.validFrom && (
                      <p>
                        <strong>Valide depuis:</strong>{" "}
                        {dayjs(legalDocument.validFrom).format("DD/MM/YYYY")}
                      </p>
                    )}
                    <p>
                      <strong>Description:</strong> {legalDocument.description}
                    </p>
                  </>
                )}
              </div>
            </header>

            <div className="space-y-6">
              {isEditMode && (
                <div className="mb-6 p-3 border border-dashed border-green-300 rounded bg-green-50/30">
                  {!showNewRootSectionInput ? (
                    <button
                      onClick={() => {
                        setShowNewRootSectionInput(true);
                        setNewRootSectionContent("");
                      }}
                      className="flex items-center gap-2 text-sm text-green-700 hover:text-green-800"
                    >
                      <Plus className="h-4 w-4" />
                      Créer une nouvelle section racine
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        value={newRootSectionContent}
                        onChange={(e) =>
                          setNewRootSectionContent(e.target.value)
                        }
                        placeholder="Contenu de la nouvelle section racine..."
                        className="w-full p-2 border rounded text-sm"
                        rows={3}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            if (newRootSectionContent.trim() && legalDocument) {
                              const newSection: LegalDocumentSection = {
                                id: generateId(),
                                orderIndex:
                                  legalDocument.rootSections.length + 1,
                                contentChunks: [
                                  {
                                    id: generateId(),
                                    position: 1,
                                    content: newRootSectionContent.trim(),
                                    embedding: "",
                                    section: {} as LegalDocumentSection,
                                  },
                                ],
                                parent: undefined,
                                children: [],
                                references: [],
                                referencedBy: [],
                                document: legalDocument,
                              };

                              // Lier le chunk à sa section
                              newSection.contentChunks[0].section = newSection;

                              legalDocument.rootSections.push(newSection);
                              setLegalDocument({ ...legalDocument });
                              setShowNewRootSectionInput(false);
                              setNewRootSectionContent("");
                              markAsDirty();
                            }
                          }}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs flex items-center gap-1"
                        >
                          <Save className="h-3 w-3" />
                          Créer
                        </button>
                        <button
                          onClick={() => {
                            setShowNewRootSectionInput(false);
                            setNewRootSectionContent("");
                          }}
                          className="px-3 py-1 bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                        >
                          <X className="h-3 w-3" />
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {legalDocument.rootSections.map((section) =>
                renderSection(section)
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
